/**
 * The box a report's map lives in: the page column by default, or the whole viewport width when
 * the viewer asks for it. The choice is one of the map adjustments the report remembers (RP5).
 * Maximised, the map covers the browser window until Escape or the button; that one is not kept.
 */
import { ActionIcon, Box, getDefaultZIndex } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { brand } from '@/theme/theme';

export interface MapFrameProps {
  testId: string;
  wide: boolean;
  onWideChange: (wide: boolean) => void;
  children: ReactNode;
}

const GUTTER = 16;
/** The frame's buttons sit in a row right of MapLibre's zoom control. */
const BUTTON_LEFT = 50;
const BUTTON_STEP = 40;
/**
 * Where something else may sit in the frame's top-left corner: below MapLibre's zoom control
 * (10 px margin, two 29 px buttons), which is the tallest thing there.
 */
export const MAP_FRAME_FREE = { top: 80, left: 10 } as const;
/**
 * Maximised, the frame covers the app shell but stays under every overlay Mantine portals to the
 * body (modals 200, popovers, comboboxes, tooltips and menus 300), so the legend, the photo viewer
 * and the layer panel's dropdowns open over the map, not behind it.
 */
export const MAXIMIZED_Z_INDEX = getDefaultZIndex('modal') - 1;

/** Escape that an open dialog or dropdown takes for itself does not also restore the map. */
function escapeIsTaken(target: EventTarget | null) {
  return target instanceof Element && (target.closest('[role="dialog"]') !== null || target.getAttribute('aria-expanded') === 'true');
}

export function MapFrame({ testId, wide, onWideChange, children }: MapFrameProps) {
  const { t } = useTranslation('viewer');
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !escapeIsTaken(e.target) && setMaximized(false);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [maximized]);
  // Breaking out of the centred page column: both edges move to the viewport's, less a gutter.
  const frame: CSSProperties = maximized
    ? { position: 'fixed', inset: 0, zIndex: MAXIMIZED_Z_INDEX, background: brand.bg }
    : wide
      ? { position: 'relative', height: 'min(85vh, 1000px)', minHeight: 420, marginLeft: `calc(50% - 50vw + ${GUTTER}px)`, marginRight: `calc(50% - 50vw + ${GUTTER}px)` }
      : { position: 'relative', height: 'min(70vh, 720px)', minHeight: 420 };
  const maximizeLabel = maximized ? t('map.restore') : t('map.maximize');
  return (
    <Box data-testid={testId} data-wide={wide || undefined} data-maximized={maximized || undefined} className="report-map" style={frame}>
      {children}
      {!maximized && (
        <ActionIcon
          className="report-map-toggle"
          variant="default"
          size="md"
          pos="absolute"
          top={10}
          left={BUTTON_LEFT}
          style={{ zIndex: 2 }}
          aria-label={wide ? t('map.normalWidth') : t('map.fillWidth')}
          title={wide ? t('map.normalWidth') : t('map.fillWidth')}
          onClick={() => onWideChange(!wide)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {wide ? <path d="M9 4v16M15 4v16M4 12h5M15 12h5" /> : <path d="M3 12h18M3 12l4-4M3 12l4 4M21 12l-4-4M21 12l-4 4" />}
          </svg>
        </ActionIcon>
      )}
      <ActionIcon
        className="report-map-toggle"
        variant="default"
        size="md"
        pos="absolute"
        top={10}
        left={maximized ? BUTTON_LEFT : BUTTON_LEFT + BUTTON_STEP}
        style={{ zIndex: 2 }}
        aria-label={maximizeLabel}
        title={maximizeLabel}
        onClick={() => setMaximized(!maximized)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {maximized ? <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /> : <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />}
        </svg>
      </ActionIcon>
    </Box>
  );
}
